import type { ReactNode } from "react";
import { Icon } from "@/components/ui/Icon/Icon";
import type { PlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import styles from "./MediaPreview.module.css";

/**
 * Placeholder treatment for IG/FB/TikTok visual content (per the design's
 * "visual honesty" rule: subtle platform-tinted stripes + monospace shot
 * briefs, never a faked finished image). Three modes: image, carousel,
 * video. Matches library-media.jsx.
 */

export type MediaType = "image" | "carousel" | "video";
export type Aspect = "1:1" | "4:5" | "9:16" | "16:9";

export interface MediaSpec {
  type?: MediaType;
  aspect?: Aspect;
  slides?: Array<{ description: string; kind?: string }>;
  cover?: string;
  description?: string;
  duration?: string;
  caption?: string;
  /**
   * Real rendered asset(s). When present, the preview shows the actual
   * generated image/video instead of the shot-brief placeholder. `assetMime`
   * decides <img> vs <video>. The brief branch below is kept verbatim as the
   * honest fallback for specs that describe an un-generated shot.
   */
  assetUrl?: string;
  assetMime?: string;
}

interface MediaPreviewProps extends MediaSpec {
  platform: PlatformKey;
  width?: number | string;
  inline?: boolean;
}

const ASPECT_CSS: Record<Aspect, string> = {
  "1:1": "1 / 1",
  "4:5": "4 / 5",
  "9:16": "9 / 16",
  "16:9": "16 / 9",
};

function placeholderBg(platform: PlatformKey): string {
  const pct = platform === "tt" ? "8%" : platform === "fb" ? "6%" : "6%";
  const varName = `var(--platform-${platform === "tt" ? "tt" : platform === "fb" ? "fb" : "ig"})`;
  const tint = `color-mix(in oklab, ${varName} ${pct}, var(--bg-subtle))`;
  return `repeating-linear-gradient(45deg, ${tint} 0 10px, var(--bg-subtle) 10px 20px)`;
}

function PreviewFrame({
  children,
  aspect = "1:1",
  platform,
  width,
  badge,
  inline,
}: {
  children: ReactNode;
  aspect?: Aspect | undefined;
  platform: PlatformKey;
  width?: number | string | undefined;
  badge?: string | undefined;
  inline?: boolean | undefined;
}) {
  return (
    <div
      className={styles.frame}
      style={{
        width: width ?? "100%",
        aspectRatio: ASPECT_CSS[aspect],
        background: placeholderBg(platform),
        borderRadius: inline ? 8 : 10,
      }}
    >
      {badge && <div className={styles.badge}>{badge}</div>}
      {children}
    </div>
  );
}

export function MediaPreview(props: MediaPreviewProps) {
  const { platform, type = "image", aspect = "4:5", inline, width } = props;

  // Real rendered asset — show the actual image/video. Falls through to the
  // shot-brief placeholder branches below when no asset is attached.
  if (props.assetUrl && props.assetMime) {
    const frameAspect: Aspect = type === "video" ? "9:16" : aspect;
    const isVideo = props.assetMime.startsWith("video/");
    return (
      <PreviewFrame
        aspect={frameAspect}
        platform={platform}
        width={width}
        inline={inline}
      >
        {isVideo ? (
          <video
            className={styles.assetMedia}
            src={props.assetUrl}
            controls
            preload="metadata"
          />
        ) : (
          <img
            className={styles.assetMedia}
            src={props.assetUrl}
            alt={props.description ?? props.caption ?? "Generated asset"}
            loading="lazy"
          />
        )}
        {props.caption && (
          <div className={styles.assetCaption}>
            <span className={styles.captionText}>{props.caption}</span>
          </div>
        )}
      </PreviewFrame>
    );
  }

  if (type === "carousel") {
    const slides = props.slides ?? [];
    const main = slides[0];
    return (
      <div className={styles.carouselWrap} style={{ width: width ?? "100%" }}>
        <PreviewFrame
          aspect={aspect}
          platform={platform}
          badge={`carousel · ${slides.length} slides`}
          inline={inline}
        >
          <div className={styles.carouselInner}>
            <div className={styles.slideCount}>1 / {slides.length}</div>
            <div className={styles.centerCol}>
              <div className={styles.iconChipLg} aria-hidden="true">
                <Icon name="image" size={18} />
              </div>
              <div className={styles.shotBrief}>{main?.description}</div>
              <div className={styles.slideLabel}>slide 1 · {main?.kind ?? "cover"}</div>
            </div>
            <div className={styles.dots}>
              {slides.map((_, i) => (
                <span key={i} className={i === 0 ? styles.dotActive : styles.dotIdle} />
              ))}
            </div>
          </div>
        </PreviewFrame>
        {!inline && slides.length > 1 && (
          <div className={styles.slideList}>
            {slides.slice(1).map((s, i) => (
              <div key={i} className={styles.slideRow}>
                <span className={styles.slideNum}>{String(i + 2).padStart(2, "0")}</span>
                <span className={styles.slideDesc}>{s.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === "video") {
    return (
      <PreviewFrame aspect="9:16" platform={platform} width={width} badge="9:16 · video" inline={inline}>
        <div className={styles.videoInner}>
          <div className={styles.videoTop}>
            {props.duration && <span className={styles.durationBadge}>{props.duration}</span>}
          </div>
          <div className={styles.centerCol}>
            <div className={styles.playBtn} aria-hidden="true">
              <Icon name="play" size={22} />
            </div>
            <div className={styles.shotBrief}>{props.cover}</div>
            <div className={styles.slideLabel}>cover frame</div>
          </div>
          {props.caption && (
            <div className={styles.captionStrip}>
              <span className={styles.captionLabel}>cap · </span>
              <span className={styles.captionText}>{props.caption}</span>
            </div>
          )}
        </div>
      </PreviewFrame>
    );
  }

  return (
    <PreviewFrame aspect={aspect} platform={platform} width={width} badge={`${aspect} · image`} inline={inline}>
      <div className={styles.imageInner}>
        <div className={styles.iconChip} aria-hidden="true">
          <Icon name="image" size={16} />
        </div>
        <div className={styles.shotBrief}>{props.description}</div>
      </div>
    </PreviewFrame>
  );
}

/** Tiny thumbnail for list rows. */
export function MediaThumb({
  platform,
  type,
  aspect = "4:5",
  count,
}: {
  platform: PlatformKey;
  type: MediaType;
  aspect?: Aspect;
  count?: number;
}) {
  const dims =
    aspect === "9:16" ? { w: 38, h: 64 } : aspect === "1:1" ? { w: 50, h: 50 } : { w: 48, h: 60 };
  const iconName = type === "video" ? "play" : type === "carousel" ? "copy" : "image";
  return (
    <div
      className={styles.thumb}
      style={{ width: dims.w, height: dims.h, background: placeholderBg(platform) }}
    >
      <Icon name={iconName} size={14} />
      {count && <span className={styles.thumbCount}>{count}</span>}
    </div>
  );
}
